import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { PROBLEM_CODES, USER_STATUS_LABELS, api, errorMessage, problemCode, useApiMutation, useApiQuery } from "../../api";
import type { Staffing, User } from "../../api";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Field, Table, useToast } from "../../components";
import type { TableColumn } from "../../components";
import { formatDate, formatTokens } from "../../format";
import { AsyncBoundary, PageHeader } from "../../shell";
import styles from "../equipe/EquipeMembres.module.css";

/**
 * Les consultants de l'ESN : les inviter, renvoyer un lien, les retirer. Pas
 * de changement de rôle ici — un consultant qui devient commercial, c'est
 * l'affaire de l'administrateur ESN, depuis l'équipe.
 */
export function EsnConsultantsPage() {
  const { notify, notifyError } = useToast();
  const liste = useApiQuery((o) => api.listConsultants(o), []);
  // Le coût de chaque consultant vit sur ses affectations, une par projet :
  // une seule lecture pour toute la liste.
  const affectations = useApiQuery((o) => api.listStaffing(o), []);
  const parConsultant = useMemo(() => {
    const m = new Map<string, Staffing[]>();
    for (const a of affectations.data?.staffing ?? []) {
      m.set(a.consultant_id, [...(m.get(a.consultant_id) ?? []), a]);
    }
    return m;
  }, [affectations.data]);
  const invitation = useApiMutation(api.inviteConsultant);
  const renvoi = useApiMutation(api.resendConsultantInvitation);
  const retrait = useApiMutation(api.removeConsultant);
  const [email, setEmail] = useState("");
  const [erreurEmail, setErreurEmail] = useState<string | null>(null);
  const [aRetirer, setARetirer] = useState<User | null>(null);

  async function inviter(e: FormEvent) {
    e.preventDefault();
    setErreurEmail(null);
    if (email.trim() === "") {
      setErreurEmail("Indiquez une adresse.");
      return;
    }
    const inv = await invitation.run({ email: email.trim() });
    if (inv === null) {
      return;
    }
    setEmail("");
    liste.reload();
    notify({
      title: inv.mail_sent ? "Invitation envoyée" : "Invitation créée, courriel non parti",
      description: inv.mail_sent
        ? `${inv.user.email} a reçu un lien valable jusqu'au ${formatDate(inv.expires_at)}.`
        : "Le courriel n'a pas pu partir. Utilisez « Renvoyer » quand le service de courriel sera rétabli.",
      tone: inv.mail_sent ? "success" : "warning",
    });
  }

  const codeInvitation = problemCode(invitation.error);
  const messageInvitation =
    codeInvitation === PROBLEM_CODES.emailTaken
      ? "Un compte porte déjà cette adresse."
      : codeInvitation === PROBLEM_CODES.invalidEmail
        ? "Cette adresse n'a pas une forme valide."
        : invitation.error !== null
          ? errorMessage(invitation.error)
          : null;

  async function renvoyer(u: User) {
    const inv = await renvoi.run(u.id);
    if (inv === null) {
      notifyError(renvoi.error, "Renvoi impossible");
      return;
    }
    notify({ title: inv.mail_sent ? "Invitation renvoyée" : "Nouveau lien créé, courriel non parti", tone: inv.mail_sent ? "success" : "warning" });
  }

  async function retirer(u: User) {
    const ok = await retrait.run(u.id);
    if (ok === null) {
      return;
    }
    setARetirer(null);
    liste.reload();
    notify({ title: `${u.email} a été retiré(e)`, tone: "neutral" });
  }

  const colonnes = useMemo<TableColumn<User>[]>(
    () => [
      { key: "email", header: "Adresse", render: (u) => <span className={styles.email}>{u.email}</span> },
      {
        key: "statut",
        header: "Statut",
        render: (u) => (
          <Badge size="sm" dot tone={u.status === "active" ? "success" : u.status === "invited" ? "warning" : "neutral"}>
            {USER_STATUS_LABELS[u.status]}
          </Badge>
        ),
      },
      {
        key: "projets",
        header: "Projets et coût",
        render: (u) => {
          // Une liste vide ne veut rien dire tant que la lecture n'a pas
          // abouti : « Aucun projet » serait une information de coût fausse.
          if (affectations.data === null) {
            return (
              <span className={styles.vide}>
                {affectations.error !== null ? "Coûts indisponibles" : "Chargement…"}
              </span>
            );
          }
          const projets = (parConsultant.get(u.id) ?? []).filter((a) => a.mission_status === "ordered");
          if (projets.length === 0) {
            return <span className={styles.vide}>Aucun projet en cours</span>;
          }
          return (
            <ul className={styles.projets}>
              {projets.map((a) => (
                <li key={a.id}>
                  <span>
                    {a.mission_label} <span className={styles.vide}>· {a.company_name} · {a.expertise_label}</span>
                  </span>
                  <strong className="lde-tabular">{formatTokens(a.tokens_per_day)} tokens/j</strong>
                </li>
              ))}
            </ul>
          );
        },
      },
      { key: "depuis", header: "Depuis", render: (u) => formatDate(u.created_at) },
      {
        key: "actions",
        header: <span className="lde-visually-hidden">Actions</span>,
        align: "right",
        render: (u) =>
          u.status === "disabled" ? null : (
            <span className={styles.actions}>
              {u.status === "invited" && (
                <Button size="sm" variant="ghost" onClick={() => void renvoyer(u)} loading={renvoi.pending}>
                  Renvoyer l'invitation
                </Button>
              )}
              <Button size="sm" variant="danger" onClick={() => setARetirer(u)}>
                Retirer
              </Button>
            </span>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- les gestes lisent les mutations par fermeture.
    [renvoi.pending, parConsultant, affectations.data, affectations.error],
  );

  return (
    <>
      <PageHeader
        eyebrow="Espace ESN"
        title="Consultants"
        description="Les intervenants qui remplissent leurs feuilles de temps. Le coût de chacun se lit par projet, au tarif de son affectation. Un consultant ne voit ni portefeuille, ni tarif : seulement les projets qu'on lui affecte."
      />
      <div className={styles.equipe}>
        <Card title="Inviter un consultant" subtitle="La personne reçoit un lien pour choisir son mot de passe. Affectez-le ensuite à un projet depuis la fiche d'une entreprise.">
          <form className={styles.formulaire} onSubmit={(e) => void inviter(e)} noValidate>
            <Field label="Adresse e-mail" error={erreurEmail ?? messageInvitation ?? undefined} required className={styles.champEmail}>
              {(commande) => (
                <input
                  {...commande}
                  type="email"
                  inputMode="email"
                  spellCheck={false}
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={invitation.pending}
                />
              )}
            </Field>
            <Button type="submit" variant="primary" loading={invitation.pending}>
              Inviter
            </Button>
          </form>
        </Card>

        <AsyncBoundary
          state={liste}
          errorTitle="Consultants illisibles"
          skeleton={<Table columns={colonnes} rows={[]} rowKey={(u) => u.id} caption="Consultants" loading animateRows={false} />}
        >
          {(donnees) => (
            <Card padding="none">
              <Table
                columns={colonnes}
                rows={donnees.users}
                rowKey={(u) => u.id}
                caption="Consultants de l'ESN, invitations en attente comprises"
                animateRows={false}
                empty={<EmptyState compact title="Aucun consultant" description="Invitez un premier consultant ci-dessus." />}
              />
            </Card>
          )}
        </AsyncBoundary>

        <ConfirmDialog
          open={aRetirer !== null}
          title="Retirer ce consultant ?"
          description={
            <>
              {aRetirer !== null && `${aRetirer.email} ne pourra plus se connecter. Ses feuilles de temps restent consultables.`}
              {retrait.error !== null && (
                <span className={styles.refus} role="alert">
                  {" "}
                  {errorMessage(retrait.error)}
                </span>
              )}
            </>
          }
          confirmLabel="Retirer"
          tone="danger"
          pending={retrait.pending}
          onConfirm={() => {
            if (aRetirer !== null) void retirer(aRetirer);
          }}
          onCancel={() => {
            setARetirer(null);
            retrait.reset();
          }}
        />
      </div>
    </>
  );
}
